/**
 * @packageDocumentation
 * Manages PixiJS asset bundles and font loading for both desktop and mobile builds, exposing helpers used by the preloader and main scene.
 */
/**
 * @module LoadAssets
 * @description Module for handling asset loading in the game
 */

import { Assets } from 'pixi.js';

/**
 * Checks if the current device is a mobile device
 * @returns {boolean} True if the device is mobile, false otherwise
 */
function isMobileDevice(): boolean {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
}

let path = "pc"
// Temporarily disabled mobile detection - always use pc assets
// if (isMobileDevice()) { 
//     path = "mobile"
// } 

/**
 * Loads initial preload assets required before game start
 * @param onProgress - Optional callback for tracking load progress
 * @returns Promise that resolves when all preload assets are loaded
 */
export async function loadPreloadAssets(onProgress?: (progress: number) => void): Promise<void> {
  Assets.addBundle('preloadAssets', {
    logo: "./assets/Images/veiledVillainsLogo.png",
    start_btn: './assets/Images/start_btn.png',
    preloader_anim_json: `./assets/spine/${path}/Bomb.json`,
    preloader_anim_atlas: `./assets/spine/${path}/Bomb.atlas`,
  });
  await Assets.loadBundle('preloadAssets');
  let supportedFontFormat = getBestSupportedFormat();
  await loadFont('American-Captain', './assets/Fonts/American-Captain.' + supportedFontFormat.ext)
  await loadFont('calibri', './assets/Fonts/calibri-regular.' + supportedFontFormat.ext);
  await loadFont('FranklinGothic-Demi', './assets/Fonts/FranklinGothic-Demi.' + supportedFontFormat.ext)
  await loadFont('Bangers-Regular', './assets/Fonts/Bangers-Regular.' + supportedFontFormat.ext)
  await loadFont(
    "PermanentMarker",
    `./assets/Fonts/PermanentMarker-Regular.ttf`
  );
  await loadFont(
    "RoadRage",
    `./assets/Fonts/RoadRage-Regular.ttf`
  );
}

/**
 * Loads all game assets required during gameplay
 * @param onProgress - Optional callback for tracking load progress
 * @returns Promise that resolves when all game assets are loaded
 */
export async function loadGameAssets(onProgress?: (progress: number) => void): Promise<void> {
  const bundleName = 'myGameAssets';
  function isMobileDevice(): boolean {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  }

  let path = "pc"
  // Temporarily disabled mobile detection - always use pc assets
  // if (isMobileDevice()) { 
  //   path = "mobile"
  // } 

  /**
   * Object containing all game asset paths 
   */
  const bundleAssets = {
    bg_preloader_wide: './assets/Images/bg_preloader_wide.png',
    Info_btn: './assets/Images/BTN_hamburg.png',
    Outer_rectangle: './assets/Images/Outer_rectangle.png',
    bonus_reel_bg: './assets/Images/bonus_reel_bg.png',
    Bonuspage_bg: './assets/Images/Bonuspage_bg.jpg',
    GameBoard: './assets/Images/gameBoard.png',
    collect_btn: './assets/Images/collect_btn.png',
    stop_btn: './assets/Images/stop_btn.png',
    popup: './assets/Images/modal_large.png',
    htp_popup: './assets/Images/htp_popup.png',
    Bet_but: './assets/Images/txtBox_bet_amount.png',
    bg_game_wide: './assets/Images/BG_villian_city.png',
    bg_game: './assets/Images/BG_veiledVillains_01.png',
    fastplay_btn_with_clk: './assets/Images/fastplay_btn_with_clk.png',
    fastplay_btn_without_clk: './assets/Images/fastplay_btn_without_clk.png',
    glow: './assets/Images/glow.png',
    Plain_btn: './assets/Images/Plain_btn.png',
    Bet_minus_but: './assets/Images/BTN_bet_less.png',
    Bet_plus_but: './assets/Images/BTN_betMore.png',
    arrow_right: './assets/Images/arrow_right.png',
    Rainbow_villain: './assets/Images/Yellow_superhero.png',
    Rainbow_civilian: './assets/spine/pc/rainbow_civilian_anim.webp',
    Orange_villain: './assets/Images/Red_superhero.png',
    Orange_civilian: './assets/Images/Red_civilian.png',
    Pink_villain: './assets/Images/Purple_superhero.png',
    Pink_civilian: './assets/Images/Purple_civilian.png',
    White_civilian: './assets/Images/Green_civilian.png',
    Teal_villain: './assets/Images/Blue_superhero.png',
    Teal_civilian: './assets/Images/Blue_civilian.png',
    silver: './assets/Images/silver.png',
    bronze: './assets/Images/bronze.png',
    halo: './assets/Images/halo.png',
    gold: './assets/Images/gold.png',
    but_autocount: './assets/Images/btn_square_yellow.png',
    but_autocount_shape: './assets/Images/btn_square_yellow_pressed.png',
    Spin_but: './assets/Images/BTN_play.png',
    bg_game_wide_top: './assets/Images/bg_game_wide_top.png',
    txtBox_totalWin: './assets/Images/txtBox_totalWin.png',
    BTN_how2Play: './assets/Images/BTN_how2Play.png',
    prizechart_popup: './assets/Images/pop_prizechart.png',
    Prize_chart_but: './assets/Images/BTN_prizeCharts.png',
    startPage_logo: './assets/Images/veiledVillainsLogo.png',
    startPage_card: './assets/Images/modal_small_rules.png',
    startPage_badge: './assets/Images/landing_bg_badge.png',
    startPage_pyramid: './assets/Images/landing_bg_pyramid.png',
    startPage_popup: './assets/Images/action_bubble_dots.png',
    Autoplay_but: './assets/Images/BTN_autoPlay.png',
    stop_autoplay_but: './assets/Images/BTN_autoPlay_stop.png',
    btn_on: './assets/Images/btn_on.png',
    btn_off: './assets/Images/btn_off.png',
    PC_Left: './assets/Images/PC_Left.png',
    PC_Right: './assets/Images/PC_Right.png',
    PC_modal_portrait: './assets/Images/ui_prizeChart_tall.png',

    winvalue_bg: './assets/Images/winvalue_bg.png',
    landing_bg_box_big: './assets/Images/modal_small_rules.png',
    but_start_autoplay: './assets/Images/btn_rect_purple.png',
    autoplay_bg: './assets/Images/modal_large.png',
    pop_volume: './assets/Images/pop_volume.png',
    btn_blank: './assets/Images/btn_blank.png',
    lp_circle: './assets/Images/or.png',
    // cash_bg :'./assets/Images/cash_bg.png', 
    // boom :'./assets/Images/boom.png',  
    bonus_bg: './assets/Images/bonus_bg.png',
    bonus_board: './assets/Images/bonus_board.png',

    bonus_reel_bg_gold: "assets/Images/bonus_reel_bg_gold.png",
    bonus_reel_bg_stone: "assets/Images/bonus_reel_bg_stone.png",
    bonus_reel_bg_brick: "assets/Images/bonus_reel_bg_brick.png",
    bonus_reel_bg_wood: "assets/Images/bonus_reel_bg_wood.png",
    bonus_reel_bg_mud: "assets/Images/bonus_reel_bg_mud.png",

    close_btn: './assets/Images/BTN_close.png',
    cloud_1: './assets/Images/cloud_1.png',
    cloud_2: './assets/Images/cloud_2.png',
    cloud_3: './assets/Images/cloud_3.png',
    // cloud_4:'./assets/Images/cloud_4.png', 
    // cloud_5:'./assets/Images/cloud_5.png', 
    // cloud_6:'./assets/Images/cloud_6.png', 
    // cloud_7:'./assets/Images/cloud_7.png', 
    // cloud_8:'./assets/Images/cloud_8.png', 
    // cloud_9:'./assets/Images/cloud_9.png', 
    // cloud_10:'./assets/Images/cloud_10.png', 
    // cloud_11:'./assets/Images/cloud_11.png',   

    backcard: './assets/Images/card_back.png',
    civilian_wild_card: './assets/Images/card_mysteryCivilian.png',
    hero_wild_card: './assets/Images/card_mysteryVillain.png',
    bannerWhite: './assets/Images/banner_whiteDots.png', // Added for 9-slice win popup 
    btn_rect_9slice: './assets/Images/btn_rect_9slice.png', // Added for 9-slice button component 

    // Spine animations
    logo_anim_json: `./assets/spine/${path}/logo_anim.json`,
    logo_anim_atlas: `./assets/spine/${path}/logo_anim.atlas`,

    Total_win_popup_anim_json: `./assets/spine/${path}/Total_win_popup_anim.json`,
    Total_win_popup_anim_atlas: `./assets/spine/${path}/Total_win_popup_anim.atlas`,


    transition_anim_json: `./assets/spine/${path}/transition_anim.json`,
    transition_anim_atlas: `./assets/spine/${path}/transition_anim.atlas`,

    start_btn_anim_json: `./assets/spine/${path}/start_btn_anim.json`,
    start_btn_anim_atlas: `./assets/spine/${path}/start_btn_anim.atlas`,

    celebration_anim_json: `./assets/spine/${path}/celebration_anim.json`,
    celebration_anim_atlas: `./assets/spine/${path}/celebration_anim.atlas`,

    Preloader_anim_json: `./assets/spine/${path}/Bomb.json`,
    Preloader_anim_atlas: `./assets/spine/${path}/Bomb.atlas`,

    bonus_blocks_anim_json: `./assets/spine/${path}/bonusGame_blocks.json`,
    bonus_blocks_anim_atlas: `./assets/spine/${path}/bonusGame_blocks.atlas`,

    win_block_json: `./assets/spine/${path}/WinBlock.json`,
    win_block_atlas: `./assets/spine/${path}/WinBlock.atlas`,

    bms_win_anim_json: `./assets/spine/${path}/bms_win_anim.json`,
    bms_win_anim_atlas: `./assets/spine/${path}/bms_win_anim.atlas`,

    teal_civilian_anim_json: `./assets/spine/${path}/teal_civilian_anim.json`,
    teal_civilian_anim_atlas: `./assets/spine/${path}/teal_civilian_anim.atlas`,

    teal_villain_anim_json: `./assets/spine/${path}/teal_villain_anim.json`,
    teal_villain_anim_atlas: `./assets/spine/${path}/teal_villain_anim.atlas`,

    white_civilian_anim_json: `./assets/spine/${path}/white_civilian_anim.json`,
    white_civilian_anim_atlas: `./assets/spine/${path}/white_civilian_anim.atlas`,

    white_villain_anim_json: `./assets/spine/${path}/white_villain_anim.json`,
    white_villain_anim_atlas: `./assets/spine/${path}/white_villain_anim.atlas`,

    pink_civilian_json: `./assets/spine/${path}/pink_civilian.json`,
    pink_civilian_atlas: `./assets/spine/${path}/pink_civilian.atlas`,

    pink_villain_anim_json: `./assets/spine/${path}/pink_villain_anim.json`,
    pink_villain_anim_atlas: `./assets/spine/${path}/pink_villain_anim.atlas`,

    orange_civilian_anim_json: `./assets/spine/${path}/orange_civilian_anim.json`,
    orange_civilian_anim_atlas: `./assets/spine/${path}/orange_civilian_anim.atlas`,

    orange_villain_anim_json: `./assets/spine/${path}/orange_villain_anim.json`,
    orange_villain_anim_atlas: `./assets/spine/${path}/orange_villain_anim.atlas`,

    rainbow_civilian_anim_json: `./assets/spine/${path}/rainbow_civilian_anim.json`,
    rainbow_civilian_anim_atlas: `./assets/spine/${path}/rainbow_civilian_anim.atlas`,

    rainbow_villain_anim_json: `./assets/spine/${path}/rainbow_villain_anim.json`,
    rainbow_villain_anim_atlas: `./assets/spine/${path}/rainbow_villain_anim.atlas`,

    bronze_frame_anim_json: `./assets/spine/${path}/cards_bronze.json`,
    bronze_frame_anim_atlas: `./assets/spine/${path}/cards_bronze.atlas`,

    Silver_frame_anim_json: `./assets/spine/${path}/cards_silver.json`,
    Silver_frame_anim_atlas: `./assets/spine/${path}/cards_silver.atlas`,

    Gold_frame_anim_json: `./assets/spine/${path}/cards_gold.json`,
    Gold_frame_anim_atlas: `./assets/spine/${path}/cards_gold.atlas`,

    Hologram_frame_anim_json: `./assets/spine/${path}/cards_hologram.json`,
    Hologram_frame_anim_atlas: `./assets/spine/${path}/cards_hologram.atlas`,

    robo_anim_json: `./assets/spine/${path}/Parrot_02.json`,
    robo_anim_atlas: `./assets/spine/${path}/Parrot_02.atlas`,

    parrot_anim_json: `./assets/spine/${path}/parrot_anim.json`,
    parrot_anim_atlas: `./assets/spine/${path}/parrot_anim.atlas`,

    bonus_transition_json: `./assets/spine/${path}/bonusTransition.json`,
    bonus_transition_atlas: `./assets/spine/${path}/bonusTransition.atlas`,

    box_shatter_anim_json: `./assets/spine/${path}/box_shatter_anim.json`,
    box_shatter_anim_atlas: `./assets/spine/${path}/box_shatter_anim.atlas`,

    beam_anim_anim_json: `./assets/spine/${path}/beam_anim_anim.json`,
    beam_anim_anim_atlas: `./assets/spine/${path}/beam_anim_anim.atlas`,

    laser_burst_anim_json: `./assets/spine/${path}/laser_burst_anim.json`,
    laser_burst_anim_atlas: `./assets/spine/${path}/laser_burst_anim.atlas`,

    powerup_anim_json: `./assets/spine/${path}/powerup_anim.json`,
    powerup_anim_atlas: `./assets/spine/${path}/powerup_anim.atlas`,

  };

  Assets.addBundle(bundleName, bundleAssets);
  const assetKeys = Object.keys(bundleAssets);
  const totalAssets = assetKeys.length;
  let loadedAssets = 0;

  const loadPromises = assetKeys.map(async (key) => {
    await Assets.load(key);
    loadedAssets++;
    if (onProgress) {
      onProgress(loadedAssets / totalAssets);
    }
  });

  await Promise.all(loadPromises);
}

/**
 * Loads a custom font using FontFace API
 * @param name - Name of the font to load
 * @param url - URL of the font file
 * @returns Promise that resolves when font is loaded
 */
export function loadFont(name: string, url: string) {
  const font = new FontFace(name, `url(${url})`);
  return font.load().then(function (loadedFont) {
    document.fonts.add(loadedFont);
    console.log(`${name} font loaded`);
  }).catch(function (error) {
    console.error(`Failed to load ${name} font`, error);
  });
}

/**
 * Interface for font format information
 */
type FontFormat = {
  extension: string;
  mimeType: string;
  ext: string;
};

/**
 * Detects the best supported font format for the current browser
 * @returns FontFormat object containing format information
 */
export function getBestSupportedFormat(): FontFormat {
  const formats: FontFormat[] = [
    { extension: "woff", mimeType: "font/woff", ext: "woff" },
    { extension: "otf", mimeType: "font/otf", ext: "opentype" },
    { extension: "ttf", mimeType: "font/ttf", ext: "truetype" },
    {
      extension: "eot",
      mimeType: "application/vnd.ms-fontobject",
      ext: "opentype",
    },
    { extension: "svg", mimeType: "image/svg+xml", ext: "svg" },
  ];

  const div = document.createElement("div");

  for (let i = 0; i < formats.length; i++) {
    try {
      div.style.font = `url(data:${formats[i].mimeType};base64,AAEAAAAKAIAAAwQgRkZUT0eRhwAAH3UAAAAHAfD1WIDFSA)`;
      if (div.style.font) {
        return formats[i];
      }
    } catch (e) {
      continue;
    }
  }

  return formats[0];
}

